import { useEffect, useState, lazy, Suspense, useMemo } from 'react'
import { PlayableList } from 'common/types'
import { getLogger } from '../../logger'
import { ContentViewer } from './ContentViewer'
const CanvasLayoutPlayer = lazy(() => import('./CanvasLayoutPlayer').then((m) => ({ default: m.CanvasLayoutPlayer })))
import { isDebugEnabled } from '../../App'
import {
  createPreloadSchedule,
  PlayScheduleItem,
  PlayScheduleItemWithSeek,
  ScheduleNotifier,
  Scheduler,
  SCHEDULER_EVENTS,
} from '../../core/scheduler'
import './Player.css'
import { useScreen } from './ScreenContainer'
import { LayoutStyleObjectFit } from '../../../../src/types/layout.types'

const log = getLogger('player')

/**
 * Generate a stable key from playables for deep comparison.
 * Changes when content IDs, durations, or schedules change.
 */
function getPlayablesKey(playables: PlayableList): string {
  return playables
    .map((p) => {
      const scheduleKey = p.schedule ? JSON.stringify(p.schedule.schedules) : ''
      return `${p.content?.id?.slice(0, 4)}-${p.duration}-${scheduleKey}`
    })
    .join('|')
}

interface PlayerProps {
  playables: PlayableList
  objectFit?: LayoutStyleObjectFit
  depth?: number
}

export const Player = ({ playables, objectFit = LayoutStyleObjectFit.FILL, depth = 0 }: PlayerProps) => {
  const [itemIndex, setItemIndex] = useState<number | undefined>(undefined)
  const [itemSeek, setItemSeek] = useState<number>(0)
  const [preloadItemIndex, setPreloadItemIndex] = useState<number | undefined>(undefined)
  const [_, setPlayNotifier] = useState<ScheduleNotifier | null>(null)
  const [__, setPreloadNotifier] = useState<ScheduleNotifier | null>(null)

  // Use screen context
  const screen = useScreen()
  const syncronized = screen?.syncronized
  const timezone = screen?.timezone

  // Stable key for deep comparison of playables
  const playablesKey = useMemo(() => getPlayablesKey(playables), [playables])

  useEffect(() => {
    log('Creating scheduler')
    const playSchedule = new Scheduler(playables, timezone).init(syncronized)
    const preloadSchedule = createPreloadSchedule(playSchedule)

    const _playNotifier = new ScheduleNotifier(playSchedule)
    const _preloadNotifier = new ScheduleNotifier(preloadSchedule)
    setPlayNotifier(_playNotifier)
    setPreloadNotifier(_preloadNotifier)

    _playNotifier.on(SCHEDULER_EVENTS.PLAY_START, (item: PlayScheduleItemWithSeek) => {
      const timeInSeconds = item.endDate.diff(item.startDate, 'seconds')
      log(
        `Playing item-${item.itemIndex} (seek: ${item.seek}) ${timeInSeconds} seconds from ${item.startDate.toISOString()} to ${item.endDate.toISOString()}`
      )

      setItemSeek(item.seek || 0)
      setItemIndex(item.itemIndex)
    })

    _playNotifier.on(SCHEDULER_EVENTS.PLAY_END, () => {
      log('No items to play')
      setItemIndex(-1)
    })

    _preloadNotifier.on(SCHEDULER_EVENTS.PLAY_START, (item: PlayScheduleItem) => {
      log(`Preloading item-${item.itemIndex}`)
      setPreloadItemIndex(item.itemIndex)
    })

    _preloadNotifier.on(SCHEDULER_EVENTS.PLAY_END, () => {
      log('Preloading finished')
      setPreloadItemIndex(undefined)
    })

    // start after all listeners are set
    _playNotifier.start()
    _preloadNotifier.start()

    return () => {
      log('Stopping notifiers')
      _playNotifier.stop()
      _preloadNotifier.stop()
    }
  }, [playablesKey])

  if (!playables.length)
    return (
      <div className="flex justify-center items-center h-full w-full bg-gradient-to-br from-slate-900 to-slate-700 text-white font-sans overflow-hidden relative">
        <div className="p-12 rounded-3xl bg-slate-900/70 backdrop-blur-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)] text-center max-w-[650px] w-4/5 border border-white/10 text-3xl font-bold">
          Assigned playlist is empty!
        </div>
      </div>
    )

  // Schedule has not been loaded yet
  if (itemIndex === undefined) return null

  const currentItem = playables[itemIndex]

  if (!currentItem) {
    return (
      <div className="flex justify-center items-center h-full w-full bg-gradient-to-br from-slate-900 to-slate-700 text-white font-sans overflow-hidden relative">
        <div className="p-12 rounded-3xl bg-slate-900/70 backdrop-blur-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)] text-center max-w-[650px] w-4/5 border border-white/10 text-3xl font-bold">
          No item is scheduled to play!
        </div>
      </div>
    )
  }

  const { content, canvasLayout } = currentItem
  const previewItem = preloadItemIndex !== undefined ? playables[preloadItemIndex] : null
  const previewContent = previewItem?.content || null
  const previewCanvasLayout = previewItem?.canvasLayout || null

  // Render canvas layout if present
  if (canvasLayout) {
    return (
      <Suspense fallback={null}>
        <div className="content-background"></div>
        <div className="player-container mx-auto">
          <CanvasLayoutPlayer layoutId={canvasLayout.id} layoutData={canvasLayout} depth={depth + 1} />
        </div>
        {previewCanvasLayout ? (
          <div className={['preview-container', isDebugEnabled() ? 'preview-debug' : ''].join(' ')}>
            <CanvasLayoutPlayer layoutId={previewCanvasLayout.id} layoutData={previewCanvasLayout} depth={depth + 1} />
          </div>
        ) : previewContent ? (
          <div className={['preview-container', isDebugEnabled() ? 'preview-debug' : ''].join(' ')}>
            <ContentViewer content={previewContent} preload objectFit={objectFit} />
          </div>
        ) : null}
      </Suspense>
    )
  }

  if (!content) {
    return (
      <div className="flex justify-center items-center h-full w-full bg-gradient-to-br from-slate-900 to-slate-700 text-white font-sans overflow-hidden relative">
        <div className="p-12 rounded-3xl bg-slate-900/70 backdrop-blur-xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)] text-center max-w-[650px] w-4/5 border border-white/10 text-3xl font-bold">
          No content available!
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="content-background" />
      <div className="player-container mx-auto">
        <ContentViewer key={itemIndex} content={content} seek={itemSeek} objectFit={objectFit} />
      </div>
      {previewCanvasLayout ? (
        <Suspense fallback={null}>
          <div className={['preview-container', isDebugEnabled() ? 'preview-debug' : ''].join(' ')}>
            <CanvasLayoutPlayer layoutId={previewCanvasLayout.id} layoutData={previewCanvasLayout} depth={depth + 1} />
          </div>
        </Suspense>
      ) : previewContent ? (
        <div className={['preview-container', isDebugEnabled() ? 'preview-debug' : ''].join(' ')}>
          <ContentViewer content={previewContent} preload objectFit={objectFit} />
        </div>
      ) : null}
    </>
  )
}
